/*
  Product gallery script

  This file owns media selection, navigation, transition, and video playback state for the main product gallery.
*/

class ProductGallery extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.stage = this.querySelector("[data-product-gallery-stage]");
    this.track = this.querySelector("[data-product-gallery-track]");
    this.slides = Array.from(this.querySelectorAll("[data-product-gallery-slide]"));
    this.thumbnails = Array.from(this.querySelectorAll("[data-product-gallery-thumbnail]"));
    this.thumbnailList = this.querySelector("[data-product-gallery-thumbnails]");
    this.status = this.querySelector("[data-product-gallery-status]");
    this.counter = this.querySelector("[data-product-gallery-counter]");
    this.videoToggle = this.querySelector("[data-product-gallery-video-toggle]");
    this.prefersReducedMotion = globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.shouldPlayVideos = this.dataset.videoAutoplay === "true" && !this.prefersReducedMotion;
    this.isInView = true;

    if (!this.stage || !this.track || !this.slides.length) return;

    this.listenerController = new AbortController();
    this.addEventListener("click", this.onClick.bind(this), {
      signal: this.listenerController.signal,
    });
    this.stage.addEventListener("click", this.onStageClickCapture.bind(this), {
      capture: true,
      signal: this.listenerController.signal,
    });
    this.addEventListener("pointerover", this.onNavigationIntent.bind(this), {
      signal: this.listenerController.signal,
    });
    this.addEventListener("focusin", this.onNavigationIntent.bind(this), {
      signal: this.listenerController.signal,
    });
    this.addEventListener("theme:intersection", this.onIntersection.bind(this), {
      signal: this.listenerController.signal,
    });

    // Media events do not bubble, so the gallery listens during capture.
    // Reduced motion steps the thumbnail progress ring on time updates instead of every frame.
    const videoEvents = ["play", "pause", "playing"];

    if (this.prefersReducedMotion) {
      videoEvents.push("timeupdate");
    }

    videoEvents.forEach((eventName) => {
      this.addEventListener(eventName, this.onVideoStateChange.bind(this), {
        capture: true,
        signal: this.listenerController.signal,
      });
    });
    this.stage.addEventListener("keydown", this.onKeydown.bind(this), {
      signal: this.listenerController.signal,
    });
    this.stage.addEventListener("pointerdown", this.onPointerDown.bind(this), {
      signal: this.listenerController.signal,
    });
    this.stage.addEventListener("pointerup", this.onPointerEnd.bind(this), {
      signal: this.listenerController.signal,
    });
    this.stage.addEventListener("pointercancel", this.onPointerEnd.bind(this), {
      signal: this.listenerController.signal,
    });

    this.currentIndex = Math.max(
      0,
      this.slides.findIndex((slide) => slide.classList.contains("is-active")),
    );
    this.thumbnailWindowStart = 0;

    if (this.thumbnailList && this.thumbnails.length) {
      this.updateThumbnailLayout();
      this.resizeObserver = new ResizeObserver(() => {
        this.updateThumbnailLayout();
      });
      this.resizeObserver.observe(this.thumbnailList);
    }

    this.isInitialized = true;
    this.showImage(this.currentIndex, false);
  }

  disconnectedCallback() {
    this.listenerController?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.thumbnailScrollFrame);
    cancelAnimationFrame(this.videoStateFrame);
    cancelAnimationFrame(this.videoProgressFrame);
    this.isInitialized = false;
  }

  onClick(event) {
    if (event.target.closest("[data-product-gallery-video-toggle]")) {
      this.toggleActiveVideo();
      return;
    }

    if (event.target.closest("[data-product-gallery-previous]")) {
      this.showImage(this.currentIndex - 1);
      return;
    }

    if (event.target.closest("[data-product-gallery-next]")) {
      this.showImage(this.currentIndex + 1);
      return;
    }

    const thumbnail = event.target.closest("[data-product-gallery-thumbnail]");

    if (thumbnail) {
      this.showImage(Number.parseInt(thumbnail.dataset.index, 10));
    }
  }

  onKeydown(event) {
    if (event.target !== this.stage) return;
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    event.preventDefault();
    this.showImage(event.key === "ArrowRight" ? this.currentIndex + 1 : this.currentIndex - 1);
  }

  onPointerDown(event) {
    this.hasSwiped = false;

    if (!event.isPrimary || event.pointerType === "mouse") return;

    const posterButton = event.target.closest("[data-deferred-video-button]");

    if (!posterButton && event.target.closest("button, a, input, select, textarea, video[controls]")) return;

    this.swipePointerId = event.pointerId;
    this.swipeStartX = event.clientX;
    this.swipeStartY = event.clientY;

    // Video posters keep the browser's implicit touch capture so a tap still reaches the poster button.
    if (!posterButton) {
      this.stage.setPointerCapture(event.pointerId);
    }
  }

  onPointerEnd(event) {
    if (event.pointerId !== this.swipePointerId) return;

    const distanceX = event.clientX - this.swipeStartX;
    const distanceY = event.clientY - this.swipeStartY;
    const isCancelled = event.type === "pointercancel";

    this.swipePointerId = null;

    if (isCancelled || Math.abs(distanceX) < 40 || Math.abs(distanceX) <= Math.abs(distanceY)) {
      return;
    }

    this.hasSwiped = true;
    this.showImage(distanceX < 0 ? this.currentIndex + 1 : this.currentIndex - 1);
  }

  onStageClickCapture(event) {
    // Keyboard clicks have no detail count and never follow a swipe.
    if (!this.hasSwiped || event.detail === 0) return;

    this.hasSwiped = false;
    event.preventDefault();
    event.stopPropagation();
  }

  onNavigationIntent(event) {
    if (event.target.closest("[data-product-gallery-previous]")) {
      this.preloadVideo(this.currentIndex - 1);
      return;
    }

    if (event.target.closest("[data-product-gallery-next]")) {
      this.preloadVideo(this.currentIndex + 1);
      return;
    }

    const thumbnail = event.target.closest("[data-product-gallery-thumbnail]");

    if (thumbnail) {
      this.preloadVideo(Number.parseInt(thumbnail.dataset.index, 10));
    }
  }

  onIntersection(event) {
    this.isInView = event.detail.isIntersecting;

    if (this.isInView) {
      if (this.shouldPlayVideos || this.shouldResumeInView) {
        this.playActiveVideo();
      }

      this.shouldResumeInView = false;
      return;
    }

    const video = this.slides[this.currentIndex].querySelector("video");

    this.shouldResumeInView = Boolean(video && !video.paused);
    video?.pause();
  }

  onVideoStateChange() {
    // Wait for the deferred video to record its loaded state before updating the controls.
    cancelAnimationFrame(this.videoStateFrame);
    this.videoStateFrame = requestAnimationFrame(() => {
      this.updateVideoState();
    });
  }

  toggleActiveVideo() {
    const video = this.slides[this.currentIndex].querySelector("video");

    if (!video) return;

    this.shouldPlayVideos = video.paused;

    if (video.paused) {
      this.playActiveVideo();
    } else {
      video.pause();
    }
  }

  playActiveVideo() {
    const slide = this.slides[this.currentIndex];
    const deferredVideo = slide.querySelector("deferred-video");
    const isDeferredVideoPending =
      deferredVideo && deferredVideo.dataset.mediaLoading !== "true" && deferredVideo.dataset.mediaLoaded !== "true";

    if (isDeferredVideoPending) {
      deferredVideo.showVideo();
      return;
    }

    slide
      .querySelector("video")
      ?.play()
      .catch(() => {});
  }

  preloadVideo(index) {
    if (navigator.connection?.saveData) return;

    const slide = this.slides[(index + this.slides.length) % this.slides.length];

    slide.querySelector("deferred-video")?.preloadVideo?.();
  }

  updateVideoState() {
    if (!this.videoToggle) return;

    const slide = this.slides[this.currentIndex];
    const video = slide.querySelector("video");
    const deferredVideo = slide.querySelector("deferred-video");
    const isVideoReady = Boolean(video && (!deferredVideo || deferredVideo.dataset.mediaLoaded === "true"));
    const isPlaying = Boolean(video && !video.paused);

    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      thumbnail.toggleAttribute("data-video-playing", isPlaying && thumbnailIndex === this.currentIndex);
    });

    this.updateVideoProgress(isPlaying ? video : null);

    this.videoToggle.hidden = !isVideoReady;
    this.videoToggle.dataset.state = isPlaying ? "playing" : "paused";
    this.videoToggle.setAttribute(
      "aria-label",
      isPlaying ? this.videoToggle.dataset.pauseLabel : this.videoToggle.dataset.playLabel,
    );
  }

  updateVideoProgress(video) {
    cancelAnimationFrame(this.videoProgressFrame);

    const thumbnail = this.thumbnails[this.currentIndex];

    if (!video || !thumbnail) return;

    const setProgress = () => {
      if (video.duration) {
        thumbnail.style.setProperty("--product-gallery-video-progress", String(video.currentTime / video.duration));
      }
    };

    setProgress();

    if (this.prefersReducedMotion) return;

    const step = () => {
      setProgress();
      this.videoProgressFrame = requestAnimationFrame(step);
    };

    this.videoProgressFrame = requestAnimationFrame(step);
  }

  showMedia(mediaId, announce = true) {
    if (!mediaId) return;

    const mediaIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));

    if (mediaIndex === -1) return;

    this.showImage(mediaIndex, announce);
  }

  showImage(index, announce = true) {
    const nextIndex = (index + this.slides.length) % this.slides.length;
    const hasChanged = nextIndex !== this.currentIndex;
    const indexDistance = Math.abs(nextIndex - this.currentIndex);
    const baseSlideDuration = 250; // Milliseconds
    const slideDurationIncrease = 150; // Milliseconds per additional slide
    const slideDuration = Math.min(
      1000,
      baseSlideDuration + Math.max(0, indexDistance - 1) * slideDurationIncrease,
    );

    this.currentIndex = nextIndex;
    this.style.setProperty("--product-gallery-slide-duration", `${slideDuration}ms`);
    this.track.style.transform = `translateX(-${nextIndex * 100}%)`;

    this.slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === nextIndex;

      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", String(!isActive));
      slide.inert = !isActive;

      if (!isActive) {
        slide.querySelector("video")?.pause();
      }
    });

    if (this.shouldPlayVideos && this.isInView) {
      this.playActiveVideo();
    }

    this.updateVideoState();

    if (announce && hasChanged) {
      this.preloadVideo(nextIndex - 1);
      this.preloadVideo(nextIndex + 1);
    }

    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      if (thumbnailIndex === nextIndex) {
        thumbnail.setAttribute("aria-current", "true");
      } else {
        thumbnail.removeAttribute("aria-current");
      }
    });

    if (announce && hasChanged) {
      this.scrollActiveThumbnail();
    }

    if (announce && this.status) {
      this.status.textContent = this.status.dataset.template
        .replace("[index]", String(nextIndex + 1))
        .replace("[count]", String(this.slides.length));
    }

    if (this.counter) {
      this.counter.textContent = `${nextIndex + 1} / ${this.slides.length}`;
    }
  }

  updateThumbnailLayout() {
    const visibleCount = this.thumbnailList.updateThumbnailLayout?.();

    if (!visibleCount) return;

    this.visibleThumbnailCount = visibleCount;

    cancelAnimationFrame(this.thumbnailScrollFrame);
    this.thumbnailScrollFrame = requestAnimationFrame(() => {
      this.scrollActiveThumbnail(false);
    });
  }

  scrollActiveThumbnail(animate = true) {
    if (!this.thumbnailList || !this.visibleThumbnailCount) return;

    const previousWindowStart = this.thumbnailWindowStart;
    const lastIndex = this.thumbnails.length - 1;
    const maxWindowStart = Math.max(0, this.thumbnails.length - this.visibleThumbnailCount);

    if (this.currentIndex <= this.thumbnailWindowStart) {
      this.thumbnailWindowStart = this.currentIndex - 1;
    } else if (this.currentIndex >= this.thumbnailWindowStart + this.visibleThumbnailCount - 1) {
      this.thumbnailWindowStart =
        this.currentIndex < lastIndex
          ? this.currentIndex - this.visibleThumbnailCount + 2
          : this.currentIndex - this.visibleThumbnailCount + 1;
    }

    this.thumbnailWindowStart = Math.max(0, Math.min(this.thumbnailWindowStart, maxWindowStart));

    if (animate && this.thumbnailWindowStart === previousWindowStart) return;

    const firstVisibleThumbnail = this.thumbnails[this.thumbnailWindowStart];

    if (!firstVisibleThumbnail) return;

    const listStyles = getComputedStyle(this.thumbnailList);
    const listBounds = this.thumbnailList.getBoundingClientRect();
    const thumbnailBounds = firstVisibleThumbnail.getBoundingClientRect();
    const isVertical = listStyles.flexDirection === "column";
    const scrollStart = isVertical ? "top" : "left";
    const currentScroll = isVertical ? this.thumbnailList.scrollTop : this.thumbnailList.scrollLeft;
    const paddingStart = Number.parseFloat(isVertical ? listStyles.paddingTop : listStyles.paddingLeft) || 0;
    const scrollOffset = currentScroll + thumbnailBounds[scrollStart] - listBounds[scrollStart] - paddingStart;

    this.thumbnailList.scrollTo({
      [scrollStart]: scrollOffset,
      behavior: animate ? listStyles.scrollBehavior : "auto",
    });
  }
}

if (!customElements.get("product-gallery")) {
  customElements.define("product-gallery", ProductGallery);
}
